import { useGetOrderActivitiesQuery } from '@codezo/graphql';
import { CreditCard, Package, Pencil, Plus, RefreshCw, Truck, XCircle } from 'lucide-react';
import {
	ActivityTimeline as ActivityTimelineView,
	type ActivityNodeStyle,
} from '../ui/activity-timeline';
import { useCurrencyStore } from '../../stores/currency';

/* ── Action → event type mapping ── */

type EventType = 'create' | 'status' | 'payment' | 'sample' | 'delivery' | 'update' | 'cancel';

const ACTION_TYPE: Record<string, EventType> = {
	ORDER_CREATED: 'create',
	ORDER_CONFIRMED: 'status',
	ORDER_CANCELLED: 'cancel',
	ORDER_UPDATED: 'update',
	ORDER_DELIVERY_ADDRESS_CHANGED: 'delivery',
	ITEM_CONFIRMED: 'status',
	ITEM_STATUS_CHANGED: 'status',
	ITEM_CANCELLED: 'cancel',
	SAMPLE_CREATED: 'sample',
	SAMPLE_STATUS_CHANGED: 'sample',
	SAMPLE_APPROVED: 'sample',
	SAMPLE_REJECTED: 'sample',
	SAMPLE_CANCELLED: 'cancel',
	SAMPLE_ROUND_APPROVED: 'sample',
	SAMPLE_PAYMENT_TREATMENT_CHANGED: 'sample',
	PAYMENT_RECEIVED: 'payment',
	PAYMENT_INVOICED: 'payment',
	PAYMENT_WAIVED: 'payment',
	DELIVERY_CREATED: 'delivery',
	DELIVERY_SHIPPED: 'delivery',
	DELIVERY_DELIVERED: 'delivery',
};

const NODE_STYLE: Record<EventType, ActivityNodeStyle> = {
	create: {
		icon: <Plus size={12} />,
		fg: 'var(--accent)',
		ring: 'var(--accent-line)',
		bg: 'var(--accent-soft)',
	},
	status: {
		icon: <RefreshCw size={12} />,
		fg: 'var(--status-info)',
		ring: 'color-mix(in srgb, var(--status-info) 42%, transparent)',
		bg: 'var(--status-info-soft)',
	},
	payment: {
		icon: <CreditCard size={12} />,
		fg: 'var(--status-success)',
		ring: 'color-mix(in srgb, var(--status-success) 32%, transparent)',
		bg: 'color-mix(in srgb, var(--status-success) 10%, transparent)',
	},
	sample: {
		icon: <Package size={12} />,
		fg: 'var(--status-warn)',
		ring: 'color-mix(in srgb, var(--status-warn) 38%, transparent)',
		bg: 'var(--status-warn-soft)',
	},
	delivery: {
		icon: <Truck size={12} />,
		fg: 'var(--accent)',
		ring: 'var(--accent-line)',
		bg: 'var(--accent-soft)',
	},
	update: {
		icon: <Pencil size={12} />,
		fg: 'var(--text-2)',
		ring: 'var(--line-3)',
		bg: 'var(--bg-4)',
	},
	cancel: {
		icon: <XCircle size={12} />,
		fg: 'var(--status-danger)',
		ring: 'color-mix(in srgb, var(--status-danger) 32%, transparent)',
		bg: 'color-mix(in srgb, var(--status-danger) 10%, transparent)',
	},
};

const resolveStyle = (action: string): ActivityNodeStyle =>
	NODE_STYLE[ACTION_TYPE[action] ?? 'update'];

/* ── Main component ── */

export function OrderActivityTimeline({ orderId }: { orderId: string }) {
	const { selectedCurrency } = useCurrencyStore();
	const { data, loading } = useGetOrderActivitiesQuery({
		variables: { orderId, inCurrency: selectedCurrency },
		fetchPolicy: 'no-cache',
	});

	const activities = data?.getOrderActivities ?? [];

	return (
		<ActivityTimelineView
			activities={activities}
			loading={loading}
			resolveStyle={resolveStyle}
		/>
	);
}
